import * as THREE from 'three';

// gyroBridge — the phone's physical orientation, as a quaternion.
//
// A plain module object like gaitBridge and talkBridge: it changes on every
// device event (60–120Hz) and nothing should re-render because someone turned
// their head. The store carries only the on/off flag, which changes twice a
// session.
//
// ── WHAT THE DEVICE ACTUALLY GIVES US ────────────────────────────────────────
// `deviceorientation` reports three Tait–Bryan angles in DEGREES:
//   alpha  rotation about Z — compass heading, 0..360
//   beta   rotation about X — front-to-back tilt, -180..180
//   gamma  rotation about Y — left-to-right tilt, -90..90
// plus `screen.orientation.angle`, because holding the phone in landscape
// rotates the SCREEN relative to the DEVICE and nothing else accounts for it.
//
// The conversion below is three.js's own DeviceOrientationControls maths, which
// was removed from core but is still the correct answer. Two multiplications
// carry the meaning:
//   q1  a -90° turn about X, because the camera looks out through the BACK of a
//       phone you are holding up, not out of its top edge.
//   q0  undoes the screen rotation, so landscape does not roll the world.
// Getting either wrong produces a world that tilts when you turn — the classic
// broken-gyro feel — rather than an obvious failure.
export const gyroBridge = {
  active: false,
  quat: new THREE.Quaternion(),
  // WHERE FORWARD IS. `alpha` is absolute — near enough a compass — so without
  // this you spawn facing magnetic north instead of facing the room. Captured
  // when the gyro is switched on: whatever way you are physically pointing
  // becomes the room's own `look` direction, which is what "plant the phone at
  // the spawn" means in practice. Re-captured by recentre().
  yawOffset: 0,
  needsRecentre: true,
};

const zee = new THREE.Vector3(0, 0, 1);
const euler = new THREE.Euler();
const q0 = new THREE.Quaternion();
const q1 = new THREE.Quaternion(-Math.sqrt(0.5), 0, 0, Math.sqrt(0.5)); // −90° about X
const D2R = Math.PI / 180;

// Screen orientation, in radians, across the two APIs that report it. Older iOS
// only has window.orientation; everything current has screen.orientation.
export function screenAngle() {
  const a = (typeof screen !== 'undefined' && screen.orientation && screen.orientation.angle)
    ?? (typeof window !== 'undefined' ? window.orientation : 0)
    ?? 0;
  return a * D2R;
}

export function setFromDeviceEvent(e) {
  if (e.alpha == null) return false;
  const alpha = e.alpha * D2R;
  const beta = (e.beta || 0) * D2R;
  const gamma = (e.gamma || 0) * D2R;
  const orient = screenAngle();

  if (gyroBridge.needsRecentre) {
    // Face the way the room wants you to face, from wherever you happen to be
    // standing in the room. The offset is applied to alpha only — pitch and roll
    // are physical facts about the phone and must not be re-based.
    gyroBridge.yawOffset = alpha;
    gyroBridge.needsRecentre = false;
  }

  euler.set(beta, alpha - gyroBridge.yawOffset, -gamma, 'YXZ');
  gyroBridge.quat.setFromEuler(euler);
  gyroBridge.quat.multiply(q1);
  gyroBridge.quat.multiply(q0.setFromAxisAngle(zee, -orient));
  return true;
}

export function recentre() { gyroBridge.needsRecentre = true; }
